import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRegisterGuild } from '@/hooks/queries'
import { useOverlayStore } from '@/store/overlays'
import { notifyError } from '@/lib/notify'
import { loadDraftGiveaway } from '@/lib/draft-storage'
import { GUILD_BOT_PERMISSIONS } from '@/lib/oauth'

/**
 * Port of pages/AuthDiscordGuild.vue — the callback after inviting the bot.
 *
 * The Vue version defined `registerGuild()` but never called it: `created()`
 * only showed the loading spinner, so returning from the Discord bot invite
 * left the app on a blank page with a spinner forever. It also reported success
 * with `color: "negative"`. Both are fixed here, and the in-progress giveaway
 * draft is restored so the user lands back in the wizard where they left off.
 */
export function AuthDiscordGuild() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const registerGuild = useRegisterGuild()
  const overlays = useOverlayStore()
  const started = useRef(false)

  const guildId = searchParams.get('guild_id')
  const permissions = searchParams.get('permissions')

  useEffect(() => {
    if (started.current) return
    started.current = true

    const backToWizard = () => {
      const draft = loadDraftGiveaway()
      if (draft) overlays.setDraftGiveaway(draft)
      overlays.open('new-giveaway')
      navigate('/', { replace: true })
    }

    if (!guildId) {
      notifyError('Unable to authorize your Discord server')
      backToWizard()
      return
    }

    if (permissions !== GUILD_BOT_PERMISSIONS) {
      notifyError(
        'Unable to verify correct permissions, please do not alter any checkboxes when authorizing',
      )
      backToWizard()
      return
    }

    registerGuild.mutateAsync({ guild_id: guildId, permissions }).finally(backToWizard)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId, permissions])

  return (
    <div className="content-row medium upper" style={{ padding: 48 }}>
      Connecting your Discord server…
    </div>
  )
}
